import { useEffect } from 'react';

import { useAuthStore } from '../../lib/authStore.js';
import { useCurrenciesStore } from '../../lib/currenciesStore.js';

export interface CurrencySelectProps {
  value: string;
  onChange: (code: string) => void;
  id?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  'aria-label'?: string;
  /** Show a leading empty option (for nullable / "any currency" fields). */
  includeEmpty?: boolean;
  emptyLabel?: string;
}

const SELECT_CLASS = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';

/**
 * A dropdown of the currencies the tenant USES (GET /currencies → the active
 * subset, managed in Settings → Currencies) — replaces the old free-text
 * 3-letter code inputs so an invalid code can't be entered.
 *
 * Always unions in the tenant base currency AND the current value, so a
 * document denominated in a currency that has since been deactivated still
 * shows its code and can be saved unchanged.
 */
export function CurrencySelect({
  value,
  onChange,
  id,
  className,
  disabled,
  required,
  includeEmpty,
  emptyLabel,
  'aria-label': ariaLabel,
}: CurrencySelectProps): JSX.Element {
  const currencies = useCurrenciesStore((s) => s.currencies);
  const base = useAuthStore((s) => s.tenant?.baseCurrencyCode ?? '');
  useEffect(() => { void useCurrenciesStore.getState().load(); }, []);
  const codes = Array.from(
    new Set([...currencies.map((c) => c.code), base, value].filter(Boolean)),
  ).sort();
  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      required={required}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      className={className ?? SELECT_CLASS}
    >
      {includeEmpty && <option value="">{emptyLabel ?? '—'}</option>}
      {codes.map((c) => (
        <option key={c} value={c}>{c}</option>
      ))}
    </select>
  );
}
